import {useEffect} from 'react';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {UserLogoutResponse} from '../../types';
import {useUserFlowState} from '../../Components/Context/UserFlow/UserFlowContext';
import {useUiState} from '../../Components/Context/UserInterface/UserInterfaceContext';
import {useErrorBoundary} from 'react-error-boundary';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {UserInterfaceActions} from '../../Components/Context/UserInterface/UserInterfaceActionTypes';
import {useSelector} from 'react-redux';
import {ProjectDirtySelector} from '../../Selectors';
import {useUserFeedback} from '../../Components/Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Components/Context/UserFeedback/UserFeedbackActionTypes';
import {useTaopix} from '../../Components/Context/TaopixContext';

export const useLogoutObserver = (server: APIServer) => {
  const taopix = useTaopix();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {showBoundary} = useErrorBoundary();
  const feedback = useUserFeedback();

  const stepActive = userFlow.isStepActive(UserFlowType.Auth, UserFlowStep.Logout);

  // if the project is dirty, prevent logging out, the useBeforeUnload hook will notify the user
  const projectDirty = useSelector(ProjectDirtySelector);

  const logout = (controller: AbortController) => {
    server
      .post<UserLogoutResponse>(
        '/api/user/logout',
        {},
        {
          token: 'token',
          json: true,
          signal: controller.signal,
          blocking: true,
        }
      )
      .then(response => {
        if ('' !== response.data.logoutUrl) {
          window.location.href = response.data.logoutUrl;
        }

        // Set the auth state back to default.
        userFlow.setStage(userFlow.getStateStage());
      })
      .catch(error => {
        if (server.isCancel(error)) return;
        showBoundary(error.message);
      })
      .finally(() => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
  };

  useEffect(() => {
    let controller;

    const handleLogout = async () => {
      controller = new AbortController();
      ui.dispatch(UserInterfaceActions.setIsBusy(true));
      try {
        if (projectDirty) {
          await taopix.core
            .get('stateManager')
            .call('autosave', {caller: 'logout', createPreviewThumbnail: false});
        }
        logout(controller);
      } catch (error: any) {
        feedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message: error.message,
            showHeader: true,
          })
        );
      }
    };

    if (stepActive && !ui.state.busy) {
      handleLogout();
    }

    return () => {
      if (controller instanceof AbortController && !ui.state.busy) {
        controller.abort();
      }
    };
  }, [stepActive]);

  return;
};
